import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiService from '../services/api';
import { Personal, UpdatePersonalData, CreatePersonalDisponibleData } from '../types';
import { standardizeName } from '../utils/formatters';

// Función para adaptar datos del backend al frontend
const adaptPersonalData = (personalBackend: any): Personal => {
  // Removed verbose debug logs to reduce runtime overhead and bundle size

  let nombre = 'Sin nombre';
  let apellido = 'Sin apellido';

  // Intentar múltiples estrategias para obtener el nombre
  const strategies = [
    // Estrategia 0: Campos nombre y apellido separados (PRIORIDAD MÁXIMA)
    () => {
      const nombreBackend = personalBackend.nombre;
      const apellidoBackend = personalBackend.apellido;
      
      // Si ambos campos existen y no están vacíos, usarlos directamente
      if (nombreBackend && apellidoBackend && 
          typeof nombreBackend === 'string' && typeof apellidoBackend === 'string' &&
          nombreBackend.trim() && apellidoBackend.trim() &&
          nombreBackend !== 'null' && apellidoBackend !== 'null') {
        return { 
          nombre: nombreBackend.trim(), 
          apellido: apellidoBackend.trim() 
        };
      }
      
      // Si solo existe nombre pero no apellido
      if (nombreBackend && typeof nombreBackend === 'string' && nombreBackend.trim() && nombreBackend !== 'null') {
        return { 
          nombre: nombreBackend.trim(), 
          apellido: apellidoBackend?.trim() || '' 
        };
      }
      
      return null;
    },

    // Estrategia 0.5: Campos nombres (V2) + apellido separados — el backend devuelve
    // "nombres" con los primeros nombres y "apellido" con los apellidos reales
    () => {
      const nombresBackend = personalBackend.nombres;
      const apellidoBackend = personalBackend.apellido;
      if (nombresBackend && apellidoBackend &&
          typeof nombresBackend === 'string' && typeof apellidoBackend === 'string' &&
          nombresBackend.trim() && apellidoBackend.trim() &&
          nombresBackend !== 'null' && apellidoBackend !== 'null') {
        return {
          nombre: nombresBackend.trim(),
          apellido: apellidoBackend.trim()
        };
      }
      return null;
    },
    
    // Estrategia 1: Columna nombres (nueva estructura)
    () => {
      if (personalBackend.nombres && typeof personalBackend.nombres === 'string' && personalBackend.nombres.trim() && personalBackend.nombres !== 'null') {
        const fullName = personalBackend.nombres.trim();
        const nameParts = fullName.split(' ');
        if (nameParts.length === 1) {
          return { nombre: nameParts[0], apellido: '' };
        } else if (nameParts.length === 2) {
          return { nombre: nameParts[0], apellido: nameParts[1] };
        } else {
          return { 
            nombre: nameParts.slice(0, -1).join(' '), 
            apellido: nameParts.slice(-1).join(' ') 
          };
        }
      }
      return null;
    },
    
    // Estrategia 2: Campo nombre directo (si viene como nombre completo en un solo campo)
    // NOTA: Esta estrategia ya NO debería ejecutarse si nombre y apellido vienen separados (Estrategia 0)
    () => {
      // Solo usar esta estrategia si no existe apellido separado
      if (personalBackend.nombre && personalBackend.nombre.trim() && !personalBackend.apellido) {
        const fullName = personalBackend.nombre.trim();
        const nameParts = fullName.split(' ');
        if (nameParts.length === 1) {
          return { nombre: nameParts[0], apellido: '' };
        } else if (nameParts.length === 2) {
          return { nombre: nameParts[0], apellido: nameParts[1] };
        } else {
          return { 
            nombre: nameParts.slice(0, -1).join(' '), 
            apellido: nameParts.slice(-1).join(' ') 
          };
        }
      }
      return null;
    },
    
    // Estrategia 3: Comentario estado
    () => {
      if (personalBackend.comentario_estado && personalBackend.comentario_estado.includes(':')) {
        const fullName = personalBackend.comentario_estado.split(':')[1]?.trim() || '';
        if (fullName) {
          const nameParts = fullName.split(' ');
          if (nameParts.length >= 2) {
            return { 
              nombre: nameParts.slice(0, -2).join(' ') || nameParts[0], 
              apellido: nameParts.slice(-2).join(' ') || nameParts.slice(1).join(' ') 
            };
          } else if (nameParts.length === 1) {
            return { nombre: nameParts[0], apellido: '' };
          }
        }
      }
      return null;
    },
    
    // Estrategia 4: Nombre completo
    () => {
      if (personalBackend.nombre_completo && personalBackend.nombre_completo.trim()) {
        const fullName = personalBackend.nombre_completo.trim();
        const nameParts = fullName.split(' ');
        if (nameParts.length === 1) {
          return { nombre: nameParts[0], apellido: '' };
        } else if (nameParts.length === 2) {
          return { nombre: nameParts[0], apellido: nameParts[1] };
        } else {
          return { 
            nombre: nameParts.slice(0, -1).join(' '), 
            apellido: nameParts.slice(-1).join(' ') 
          };
        }
      }
      return null;
    },
    
    // Estrategia 5: Buscar en otros campos posibles
    () => {
      // Buscar en campos que podrían contener nombres
      const possibleFields = ['full_name', 'fullName', 'name', 'nombre_persona', 'persona_nombre'];
      for (const field of possibleFields) {
        if (personalBackend[field] && personalBackend[field].trim()) {
          const fullName = personalBackend[field].trim();
          const nameParts = fullName.split(' ');
          if (nameParts.length === 1) {
            return { nombre: nameParts[0], apellido: '' };
          } else if (nameParts.length === 2) {
            return { nombre: nameParts[0], apellido: nameParts[1] };
          } else {
            return { 
              nombre: nameParts.slice(0, -1).join(' '), 
              apellido: nameParts.slice(-1).join(' ') 
            };
          }
        }
      }
      return null;
    },
    
    // Estrategia 6: Buscar en campos de texto libre
    () => {
      // Buscar en campos que podrían contener información de nombres
      const textFields = ['descripcion', 'description', 'observaciones', 'notes', 'info'];
      for (const field of textFields) {
        if (personalBackend[field] && personalBackend[field].trim()) {
          const text = personalBackend[field].trim();
          // Buscar patrones como "Nombre: Juan Pérez" o "Juan Pérez - Cargo"
          const nameMatch = text.match(/(?:nombre|name)[:\s]+([a-zA-ZáéíóúÁÉÍÓÚñÑ\s]+)/i);
          if (nameMatch) {
            const fullName = nameMatch[1].trim();
            const nameParts = fullName.split(' ');
            if (nameParts.length === 1) {
              return { nombre: nameParts[0], apellido: '' };
            } else if (nameParts.length === 2) {
              return { nombre: nameParts[0], apellido: nameParts[1] };
            } else {
              return { 
                nombre: nameParts.slice(0, -1).join(' '), 
                apellido: nameParts.slice(-1).join(' ') 
              };
            }
          }
        }
      }
      return null;
    },
    
    // Estrategia 7: Usar RUT como identificador (última opción)
    () => {
      if (personalBackend.rut) {
        return { nombre: `Personal ${personalBackend.rut}`, apellido: '' };
      }
      return null;
    }
  ];

  // Probar cada estrategia hasta encontrar una que funcione
  for (let i = 0; i < strategies.length; i++) {
    const result = strategies[i]();
    if (result) {
      nombre = result.nombre;
      apellido = result.apellido;
      break;
    }
  }
  // Normalizar contacto de emergencia en una variable para evitar expresiones complejas inline
  let contactoEmerg: any = undefined;
  if (personalBackend.nombre_contacto_emergencia || personalBackend.telefono_contacto_emergencia || personalBackend.vinculo_contacto_emergencia || personalBackend.contacto_emergencia) {
    const raw = personalBackend.contacto_emergencia;
    let cNombre = personalBackend.nombre_contacto_emergencia || undefined;
    let cRelacion = personalBackend.vinculo_contacto_emergencia || undefined;
    let cTelefono = personalBackend.telefono_contacto_emergencia || undefined;
    const cEmail = personalBackend.email_contacto_emergencia || undefined;
    if (raw && typeof raw === 'object') {
      // Ya viene como objeto parseado
      if (!cNombre) cNombre = (raw as any).nombre || undefined;
      if (!cRelacion) cRelacion = (raw as any).relacion || undefined;
      if (!cTelefono) cTelefono = (raw as any).telefono || undefined;
    } else if (!cNombre && typeof raw === 'string') {
      // Intentar parsear como JSON primero
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          cNombre = parsed.nombre || undefined;
          if (!cRelacion) cRelacion = parsed.relacion || undefined;
          if (!cTelefono) cTelefono = parsed.telefono || undefined;
        }
      } catch {
        // Formato legacy "nombre - telefono"
        const parts = raw.split('-').map((p: string) => p.trim());
        if (parts.length >= 1 && parts[0]) cNombre = parts[0];
        if (!cRelacion && parts.length >= 2 && parts[1]) cRelacion = 'Contacto';
        if (!cTelefono && parts.length >= 2 && parts[1]) cTelefono = parts[1];
      }
    }
    contactoEmerg = { nombre: cNombre, relacion: cRelacion, telefono: cTelefono, email: cEmail };
  }

  return {
    ...personalBackend,
    id: personalBackend.rut || personalBackend.id,
    nombre,
    apellido,
    // nombres es el nombre completo auto-generado por trigger del backend — fuente más confiable
    display_name: personalBackend.nombres
      ? standardizeName(personalBackend.nombres.trim())
      : personalBackend.display_name && personalBackend.apellido
        ? `${personalBackend.display_name} ${personalBackend.apellido}`.trim()
        : personalBackend.display_name || `${nombre} ${apellido}`.trim() || personalBackend.nombre_completo || undefined,
    rut: personalBackend.rut || personalBackend.id,
    // Marcar si el registro viene como fijo/asignado desde backend (varias posibles keys)
    fijo: Boolean(
      personalBackend.fijo || personalBackend.is_fijo || personalBackend.asignado || personalBackend.is_assigned || personalBackend.assigned || false
    ),
    asignado: Boolean(
      personalBackend.asignado || personalBackend.is_assigned || personalBackend.assigned || personalBackend.fijo || false
    ),
    activo: ['activo', 'disponible', 'en servicio', 'activo en servicio'].includes(
      (personalBackend.estado_nombre || personalBackend.estado || '').toString().toLowerCase().trim()
    ) || Boolean(personalBackend.activo) || personalBackend.estado_id === 1,
    email: personalBackend.correo_electronico || personalBackend.email || personalBackend.correo || undefined,
    telefono: personalBackend.telefono || personalBackend.telefono_2 || personalBackend.phone || undefined,
    contacto: personalBackend.contacto_emergencia || undefined,
    profile_image_url: personalBackend.profile_image_url || personalBackend.profile_image || undefined,
    created_at: personalBackend.created_at || new Date().toISOString(),
    updated_at: personalBackend.updated_at || new Date().toISOString(),
    empresa_id: personalBackend.empresa_id || '1',
    servicio_id: personalBackend.servicio_id || '1',
    fecha_inicio_contrato: personalBackend.fecha_inicio_contrato || personalBackend.fecha_inicio || undefined,
    zona_geografica: personalBackend.zona_geografica || personalBackend.zona || undefined,
    fecha_nacimiento: personalBackend.fecha_nacimiento || personalBackend.nacimiento || undefined,
    talla_zapatos: personalBackend.talla_zapatos || personalBackend.talla_zapato || personalBackend.talla_z || undefined,
    talla_pantalon: personalBackend.talla_pantalon || personalBackend.talla_pantalones || personalBackend.talla_p || undefined,
    // Preserve explicit null from backend for `talla_ropa` so displayValue can show '-'.
    talla_poleras: Object.prototype.hasOwnProperty.call(personalBackend, 'talla_ropa')
      ? personalBackend.talla_ropa
      : (personalBackend.talla_poleras ?? personalBackend.talla_polera ?? personalBackend.talla_camisa ?? undefined),
    // Información adicional del ejemplo de backend
    profesion: personalBackend.profesion || personalBackend.profesión || undefined,
    area: personalBackend.area || personalBackend.id_area || undefined,
    supervisor: personalBackend.supervisor || personalBackend.nombre_supervisor || undefined,
    tipo_asistencia: personalBackend.tipo_asistencia || personalBackend.asistencia || undefined,
    // Contacto de emergencia ya normalizado
    contacto_emergencia: contactoEmerg,
    // Ubicación detallada
    ubicacion: {
      pais: personalBackend.pais || undefined,
      region: personalBackend.region || undefined,
      ciudad: personalBackend.ciudad || undefined,
      comuna: personalBackend.comuna || undefined,
      direccion: personalBackend.direccion || personalBackend.domicilio || undefined,
    },
    empresa: {
      id: personalBackend.empresa_id ? String(personalBackend.empresa_id) : '1',
      nombre: personalBackend.empresa || personalBackend.empresa_nombre || personalBackend.empresa_name || personalBackend.zona_geografica || ''
    }
  };
};

// Hook para obtener lista de personal con paginación y filtros.
//
// `options.enabled` existe para los consumidores que se montan antes de que haya sesión
// resuelta — lo usa useAllDocumentos para la campana de Pre-SISI. Es opcional y por defecto
// true, así que ningún llamador existente cambia de comportamiento.
export const usePersonalList = (
  page = 1,
  limit = 10,
  search = '',
  filters: any = {},
  options: { enabled?: boolean; serverPaginated?: boolean } = {}
) => {
  // Keep calls quiet by default to avoid noisy logs in production

  return useQuery({
    enabled: options.enabled ?? true,
    queryKey: ['personal', 'list', page, limit, search, filters, Boolean(options.serverPaginated)],
    queryFn: async () => {
      // Si se solicita información de centro, usar endpoint especializado
      if (filters && filters.withCentro) {
        const resp = await apiService.getPersonalWithCentro(page, limit, search, filters);
        const itemsRaw = resp.data || [];
        // Mapear conservando campos de centro y adaptando el resto
        const items: Personal[] = itemsRaw.map((it: any) => {
          const base = adaptPersonalData(it);
          return {
            ...base,
            id_centro_costo: it.id_centro_costo || it.centro_costo_id || it.id_centro,
            centro_costo: it.centro_costo || it.nombre_centro || it.centro,
            sede: it.sede || it.sucursal || undefined,
            // En /with-centro `nombre`/`apellido` traen solo el primer nombre y el
            // primer apellido, y el `display_name` que envía el backend puede venir
            // recortado. `adaptPersonalData` ya calcula el nombre completo a partir
            // de `nombres`, así que lo preferimos para no mostrar el nombre cortado
            // al filtrar por centro.
            display_name: base.display_name || it.display_name || `${base.nombre} ${base.apellido}`.trim()
          } as Personal;
        });

        const total = resp.pagination?.total ?? items.length;
        const respLimit = resp.pagination?.limit ?? limit;
        const respOffset = resp.pagination?.offset ?? ((page - 1) * limit);

        return {
          success: true,
          data: {
            items,
            total,
            page: Math.floor(respOffset / respLimit) + 1,
            limit: respLimit,
            totalPages: Math.max(1, Math.ceil(total / respLimit))
          },
          message: 'Personal con centro obtenido'
        };
      }

      const response = options.serverPaginated
        ? await apiService.getPersonalPagina(page, limit, search, filters)
        : await apiService.getPersonal(page, limit, search, JSON.stringify(filters));
      // Adaptar la estructura de respuesta del backend al frontend
      return {
        success: response.success,
        data: {
          items: response.data.map(adaptPersonalData),
          total: response.pagination.total,
          page: Math.floor(response.pagination.offset / response.pagination.limit) + 1,
          limit: response.pagination.limit,
          totalPages: Math.ceil(response.pagination.total / response.pagination.limit)
        },
        message: response.message
      };
    },
    staleTime: 60 * 1000, // 1 minuto: reduce refetch frequency while keeping reasonably fresh data
    cacheTime: 5 * 60 * 1000,
    keepPreviousData: true,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    // Refrescar automáticamente cuando cambie el término de búsqueda
    refetchOnWindowFocus: false,
    refetchOnMount: true,
  });
};

// Hook para obtener personal por ID
export const usePersonalById = (id: string) => {
  return useQuery({
    queryKey: ['personal', 'detail', id],
    queryFn: () => apiService.getPersonalById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

// Hook para crear personal (usando personal-disponible)
export const useCreatePersonal = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePersonalDisponibleData) => apiService.createPersonalDisponible(data),
    onSuccess: async (response, variables) => {
      // Sincronizar el nombre a la tabla `nombres` (igual que useUpdatePersonal).
      // `/personal-disponible` no persiste el nombre: vive en `/nombres` (sync Google Drive).
      try {
        const created = (response && ((response as any).data || response)) || {};
        const rut = (created as any)?.rut || variables?.rut;
        const nombreVal = variables?.nombre || (created as any)?.nombre || '';
        const apellidoVal = variables?.apellido || (created as any)?.apellido || '';
        const nombresLegacy = variables?.nombres || `${nombreVal} ${apellidoVal}`.trim();

        if (rut && (nombreVal || apellidoVal || nombresLegacy)) {
          const payload: any = {
            rut,
            nombre: nombreVal,
            apellido: apellidoVal,
            // Fallback legacy: si no hay nombre/apellido separados, enviar nombres concatenado
            ...((!nombreVal || !apellidoVal) && nombresLegacy ? { nombres: nombresLegacy } : {}),
          };

          // `/personal-disponible` ya crea una fila stub en `nombres` al crear el
          // personal, por eso intentamos PUT primero (igual que el flujo de edición)
          // y dejamos POST como respaldo. Así evitamos el 409 Conflict del create.
          try {
            console.log('🔍 Intentando PUT /nombres tras crear, payload:', payload);
            const updResp = await apiService.updateNombre(String(rut), {
              nombre: nombreVal,
              apellido: apellidoVal,
              ...((!nombreVal || !apellidoVal) && nombresLegacy ? { nombres: nombresLegacy } : {}),
            });
            console.log('✅ updateNombre response:', updResp);
          } catch (updErr: any) {
            // Si la fila aún no existía, crearla
            console.warn('⚠️ updateNombre falló, intentando createNombre:', updErr?.response?.status);
            try {
              const createResp = await apiService.createNombre(payload);
              console.log('✅ createNombre response:', createResp);
            } catch (createErr: any) {
              console.warn('⚠️ createNombre también falló:', createErr?.response?.data);
            }
          }
        }
      } catch (e) {
        console.warn('⚠️ No se pudo sincronizar el nombre tras crear personal:', e);
      }

      queryClient.invalidateQueries({ queryKey: ['personal'] });
    },
  });
};

// Hook para actualizar personal
export const useUpdatePersonal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePersonalData }) => 
      apiService.updatePersonal(id, data),
    onSuccess: async (response, { id }) => {
      // Invalidar todas las queries relacionadas con 'personal' para forzar refetch
      queryClient.invalidateQueries(['personal']);
      queryClient.invalidateQueries(['personal', 'detail', id]);
      try {
        console.log('🔍 useUpdatePersonal onSuccess - raw response:', response, 'input id:', id);
        const updated = (response && (response.data || response)) || null;
        const rut = updated?.rut || id;
        const nombre = (updated as any)?.nombre || (updated as any)?.nombres || `${(updated as any)?.nombre || ''} ${(updated as any)?.apellido || ''}`.trim();
        // Dispatch a global event so notifications system can create a low-priority notification
        const ev = new CustomEvent('personalUpdated', { detail: { id: rut, rut, nombre, updated } });
        window.dispatchEvent(ev);

        // Aplicar cambio inmediato en caché de react-query para todas las queries de lista
        try {
          const listQueries = queryClient.getQueriesData(['personal', 'list']);
          listQueries.forEach(([queryKey, data]) => {
            try {
              queryClient.setQueryData(queryKey as any, (old: any) => {
                if (!old || !old.data || !Array.isArray(old.data.items)) return old;
                const items = old.data.items.map((it: any) => {
                  const itRut = it.rut || it.id || it.rut_persona || '';
                  if (!itRut) return it;
                  if (String(itRut).replace(/\./g, '') === String(rut).replace(/\./g, '')) {
                    return { ...it, nombres: nombre, nombre, apellido: (updated as any)?.apellido || it.apellido };
                  }
                  return it;
                });
                return { ...old, data: { ...old.data, items } };
              });
            } catch (e) {
              // ignore per-item cache errors
            }
          });
        } catch (e) {
          // ignore
        }

        // Sincronizar la tabla `nombres` usando el formato recomendado (nombre/apellido separados)
        // El backend ahora acepta nombre+apellido y genera automáticamente el campo `nombres` vía trigger
        try {
          if (rut) {
            const nombreVal = (updated as any)?.nombre || '';
            const apellidoVal = (updated as any)?.apellido || '';
            
            // Enviar formato recomendado: nombre y apellido separados
            const payload = {
              nombre: nombreVal,
              apellido: apellidoVal,
              // Fallback legacy: si no hay nombre/apellido, usar nombres concatenado
              ...((!nombreVal || !apellidoVal) && nombre ? { nombres: nombre } : {})
            };
            
            console.log('🔍 Intentando PUT /nombres/', rut, 'payload (nombre/apellido):', payload);
            const updResp = await apiService.updateNombre(String(rut), payload);
            console.log('✅ updateNombre response:', updResp);
          }
        } catch (syncErr: any) {
          console.warn('⚠️ updateNombre falló:', syncErr);
          console.warn('⚠️ updateNombre response data:', syncErr?.response?.data);
          console.warn('⚠️ updateNombre response status:', syncErr?.response?.status);
          // Si falla, intentar crear el registro de nombres (por si no existía)
          try {
            if (rut) {
              const nombreVal = (updated as any)?.nombre || '';
              const apellidoVal = (updated as any)?.apellido || '';
              
              const payload = {
                rut,
                nombre: nombreVal,
                apellido: apellidoVal,
                // Fallback legacy
                ...((!nombreVal || !apellidoVal) && nombre ? { nombres: nombre } : {})
              };
              
              console.log('🔍 Intentando POST /nombres con (nombre/apellido):', payload);
              const createResp = await apiService.createNombre(payload);
              console.log('✅ createNombre response:', createResp);
            }
          } catch (createErr) {
            console.warn('⚠️ createNombre también falló:', createErr);
            console.warn('⚠️ createNombre response data:', (createErr as any)?.response?.data);
            console.warn('⚠️ createNombre response status:', (createErr as any)?.response?.status);
          }
        }
      } catch (err) {
        console.warn('Error dispatching personalUpdated event', err);
      }
    },
  });
};

// Hook para eliminar personal
export const useDeletePersonal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => apiService.deletePersonal(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal'] });
      queryClient.invalidateQueries({ queryKey: ['personal-nuevos-31'] });
      queryClient.invalidateQueries({ queryKey: ['personal-disponible-servicio'] });
    },
  });
};

// Hook para obtener lista de centros de costo desde el backend
export const useCentrosCosto = () => {
  return useQuery({
    queryKey: ['centros-costo'],
    queryFn: () => apiService.getCentrosCosto(),
    staleTime: 10 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  });
};

// Hook para obtener/actualizar disponibilidad
export const usePersonalAvailability = (id: string) => {
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: ['personal', 'availability', id],
    queryFn: () => apiService.getPersonalDisponibilidad(id),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (disponibilidad: any) => apiService.updatePersonalDisponibilidad(id, disponibilidad),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['personal', 'availability', id] });
    },
  });

  return {
    ...query,
    updateAvailability: updateMutation.mutate,
    isUpdating: updateMutation.isLoading,
  };
};
